import { useEffect, useState } from "react";
import axios from "axios";
import {
  Briefcase,
  CheckCheck,
  Download,
  Eye,
  Inbox,
  Loader2,
  Mail,
  MessageSquare,
  Phone,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { Eyebrow, Reveal } from "@/components/Reveal";
import JobsPanel from "@/components/JobsPanel";
import SiteContentPanel from "@/components/SiteContentPanel";
import TeamPanel from "@/components/TeamPanel";
import ActivityPanel from "@/components/ActivityPanel";
import NewsPanel from "@/components/NewsPanel";
import { useAuth } from "@/context/AuthContext";

import { API } from "@/lib/api";

const STATUS_STYLES = {
  new: "bg-gold/15 text-gold-dark",
  read: "bg-mist text-fog",
  replied: "bg-emerald-50 text-emerald-600",
};

const FILTERS = ["all", "new", "read", "replied"];
const APP_FILTERS = ["all", "new", "shortlisted", "rejected", "hired"];

const APP_STATUS_STYLES = {
  new: "bg-gold/15 text-gold-dark",
  shortlisted: "bg-royal/10 text-royal",
  rejected: "bg-red-50 text-red-600",
  hired: "bg-emerald-50 text-emerald-600",
};

const formatDate = (iso) =>
  new Date(iso).toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const Admin = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState("enquiries");
  const [items, setItems] = useState(null);
  const [applications, setApplications] = useState(null);
  const [filter, setFilter] = useState("all");
  const [appFilter, setAppFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [replyOpen, setReplyOpen] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [noteOpen, setNoteOpen] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [team, setTeam] = useState([]);

  const loadEnquiries = () =>
    axios
      .get(`${API}/contact`, { withCredentials: true })
      .then((r) => setItems(r.data))
      .catch(() => setItems([]));

  const loadApplications = () =>
    axios
      .get(`${API}/applications`, { withCredentials: true })
      .then((r) => setApplications(r.data))
      .catch(() => setApplications([]));

  useEffect(() => {
    loadEnquiries();
    loadApplications();
    if (isAdmin) {
      axios
        .get(`${API}/team`, { withCredentials: true })
        .then((r) => setTeam(r.data))
        .catch(() => {});
    }
  }, [isAdmin]);

  const assignEnquiry = async (id, assignee) => {
    try {
      await axios.patch(
        `${API}/contact/${id}/assign`,
        { assignee },
        { withCredentials: true }
      );
      toast.success(assignee ? `Assigned to ${assignee}` : "Enquiry unassigned");
      loadEnquiries();
    } catch {
      toast.error("Could not assign the enquiry");
    }
  };

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/contact/${id}`, { status }, { withCredentials: true });
      toast.success(`Marked as ${status}`);
      loadEnquiries();
    } catch {
      toast.error("Could not update the enquiry");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this enquiry permanently?")) return;
    try {
      await axios.delete(`${API}/contact/${id}`, { withCredentials: true });
      toast.success("Enquiry deleted");
      loadEnquiries();
    } catch {
      toast.error("Could not delete the enquiry");
    }
  };

  const sendReply = async (id) => {
    if (!replyText.trim()) return;
    setSending(true);
    try {
      await axios.post(
        `${API}/contact/${id}/reply`,
        { message: replyText },
        { withCredentials: true }
      );
      toast.success("Reply sent from your official email");
      setReplyOpen(null);
      setReplyText("");
      loadEnquiries();
    } catch {
      toast.error("Could not send the reply");
    } finally {
      setSending(false);
    }
  };

  const setAppStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/applications/${id}`, { status }, { withCredentials: true });
      toast.success(`Candidate marked as ${status}`);
      loadApplications();
    } catch {
      toast.error("Could not update the application");
    }
  };

  const saveNote = async (id) => {
    try {
      await axios.put(
        `${API}/applications/${id}/note`,
        { note: noteText },
        { withCredentials: true }
      );
      toast.success("Note saved");
      setNoteOpen(null);
      loadApplications();
    } catch {
      toast.error("Could not save the note");
    }
  };

  const downloadResume = async (a) => {
    try {
      const r = await axios.get(`${API}/applications/${a.id}/resume`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = a.resume_filename || "resume";
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download the resume");
    }
  };

  const visible =
    items === null
      ? null
      : items.filter((m) => {
          if (filter !== "all" && (m.status || "new") !== filter) return false;
          if (!search.trim()) return true;
          const q = search.trim().toLowerCase();
          return (
            m.name.toLowerCase().includes(q) ||
            m.email.toLowerCase().includes(q) ||
            m.subject.toLowerCase().includes(q)
          );
        });

  const visibleApps =
    applications === null
      ? null
      : applications.filter(
          (a) => appFilter === "all" || (a.status || "new") === appFilter
        );

  return (
    <section data-testid="admin-inbox" className="min-h-screen bg-mist pb-24 pt-36">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <Reveal>
          <Eyebrow>Admin</Eyebrow>
          <h1 className="mt-4 font-heading text-3xl font-bold tracking-tight text-ink sm:text-4xl">
            {tab === "enquiries" ? "Enquiries Inbox" : "Job Applications"}
          </h1>
        </Reveal>

        <div className="mt-8 flex gap-2" data-testid="admin-tabs">
          {[
            ...((isAdmin || (user?.permissions || ["enquiries", "applications"]).includes("enquiries"))
              ? [{ key: "enquiries", label: `Enquiries${items ? ` (${items.length})` : ""}` }]
              : []),
            ...((isAdmin || (user?.permissions || ["enquiries", "applications"]).includes("applications"))
              ? [{ key: "applications", label: `Applications${applications ? ` (${applications.length})` : ""}` }]
              : []),
            ...(isAdmin
              ? [
                  { key: "jobs", label: "Job Postings" },
                  { key: "news", label: "News Posts" },
                  { key: "content", label: "Site Content" },
                  { key: "team", label: "Team" },
                  { key: "activity", label: "Activity Log" },
                ]
              : []),
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`tab-${t.key}`}
              className={`rounded-full px-6 py-2.5 font-heading text-xs font-bold uppercase tracking-widest transition-all duration-300 ${
                tab === t.key
                  ? "bg-navy text-white"
                  : "border border-gray-200 bg-white text-fog hover:border-navy hover:text-navy"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "enquiries" ? (
          <>
            {items && items.length > 0 && (
              <>
                <div className="mt-6">
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by name, email or subject…"
                    data-testid="enquiry-search"
                    className="w-full max-w-md rounded-full border border-gray-200 bg-white px-5 py-3 text-sm outline-none transition-all duration-300 placeholder:text-gray-400 focus:border-navy focus:ring-4 focus:ring-navy/10"
                  />
                </div>
                <div className="mt-4 flex flex-wrap gap-2" data-testid="enquiry-filters">
                  {FILTERS.map((f) => {
                    const count =
                      f === "all"
                        ? items.length
                        : items.filter((m) => (m.status || "new") === f).length;
                    return (
                      <button
                        key={f}
                        onClick={() => setFilter(f)}
                        data-testid={`filter-${f}`}
                        className={`rounded-full px-5 py-2 text-xs font-bold uppercase tracking-widest transition-all duration-300 ${
                          filter === f
                            ? "bg-navy text-white"
                            : "border border-gray-200 bg-white text-fog hover:border-navy hover:text-navy"
                        }`}
                      >
                        {f} ({count})
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            {visible === null ? (
              <div className="flex justify-center py-24">
                <Loader2 className="h-8 w-8 animate-spin text-navy" />
              </div>
            ) : visible.length === 0 ? (
              <div className="mt-10 flex flex-col items-center rounded-3xl border border-gray-100 bg-white py-20 text-center">
                <Inbox className="h-10 w-10 text-gray-300" />
                <p className="mt-4 font-heading text-lg font-bold text-ink">No enquiries here</p>
                <p className="mt-1 text-sm text-fog">New contact-form messages will appear here.</p>
              </div>
            ) : (
              <div className="mt-6 space-y-4">
                {visible.map((m) => {
                  const status = m.status || "new";
                  return (
                    <Reveal key={m.id}>
                      <article
                        data-testid="enquiry-card"
                        className="rounded-3xl border border-gray-100 bg-white p-7 shadow-sm"
                      >
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <div className="flex flex-wrap items-center gap-3">
                              <h2 className="font-heading text-lg font-bold text-ink">{m.subject}</h2>
                              <span
                                data-testid={`status-${status}`}
                                className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest ${STATUS_STYLES[status]}`}
                              >
                                {status}
                              </span>
                              {m.assigned_to && (
                                <span className="rounded-full bg-royal/10 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-royal">
                                  → {m.assigned_to}
                                </span>
                              )}
                              {isAdmin && team.length > 0 && (
                                <select
                                  value={m.assigned_to || ""}
                                  onChange={(e) => assignEnquiry(m.id, e.target.value)}
                                  data-testid={`assign-${m.id}`}
                                  className="rounded-full border border-gray-200 bg-white px-3 py-1 text-[10px] font-semibold text-fog outline-none transition-all duration-300 hover:border-navy"
                                >
                                  <option value="">Assign to…</option>
                                  {team.map((t) => (
                                    <option key={t.email} value={t.email}>
                                      {t.name} ({t.role})
                                    </option>
                                  ))}
                                </select>
                              )}
                            </div>
                            <p className="mt-1 text-sm font-medium text-navy">{m.name}</p>
                            <div className="mt-2 flex flex-wrap gap-4 text-xs text-fog">
                              <span className="flex items-center gap-1.5">
                                <Mail className="h-3.5 w-3.5 text-gold" /> {m.email}
                              </span>
                              {m.phone && (
                                <span className="flex items-center gap-1.5">
                                  <Phone className="h-3.5 w-3.5 text-gold" /> {m.phone}
                                </span>
                              )}
                              {m.company && <span>Company: {m.company}</span>}
                            </div>
                          </div>
                          <span className="rounded-full bg-mist px-3 py-1 text-[11px] font-semibold text-fog">
                            {formatDate(m.created_at)}
                          </span>
                        </div>
                        <p className="mt-4 whitespace-pre-line rounded-2xl bg-mist p-5 text-sm leading-relaxed text-ink">
                          {m.message}
                        </p>
                        <div className="mt-5 flex flex-wrap gap-2">
                          <button
                            onClick={() => {
                              setReplyOpen(replyOpen === m.id ? null : m.id);
                              setReplyText("");
                            }}
                            data-testid={`reply-${m.id}`}
                            className="flex items-center gap-1.5 rounded-full bg-navy px-4 py-2 text-xs font-semibold text-white transition-all duration-300 hover:bg-navy-800"
                          >
                            <MessageSquare className="h-3.5 w-3.5" /> Reply
                          </button>
                          {status !== "read" && (
                            <button
                              onClick={() => setStatus(m.id, "read")}
                              data-testid={`mark-read-${m.id}`}
                              className="flex items-center gap-1.5 rounded-full border border-gray-200 px-4 py-2 text-xs font-semibold text-fog transition-all duration-300 hover:border-royal hover:text-royal"
                            >
                              <Eye className="h-3.5 w-3.5" /> Mark Read
                            </button>
                          )}
                          {status !== "replied" && (
                            <button
                              onClick={() => setStatus(m.id, "replied")}
                              data-testid={`mark-replied-${m.id}`}
                              className="flex items-center gap-1.5 rounded-full border border-gray-200 px-4 py-2 text-xs font-semibold text-fog transition-all duration-300 hover:border-emerald-500 hover:text-emerald-600"
                            >
                              <CheckCheck className="h-3.5 w-3.5" /> Mark Replied
                            </button>
                          )}
                          <button
                            onClick={() => remove(m.id)}
                            data-testid={`delete-${m.id}`}
                            className="flex items-center gap-1.5 rounded-full border border-gray-200 px-4 py-2 text-xs font-semibold text-fog transition-all duration-300 hover:border-red-400 hover:text-red-600"
                          >
                            <Trash2 className="h-3.5 w-3.5" /> Delete
                          </button>
                        </div>
                        {replyOpen === m.id && (
                          <div className="mt-4 rounded-2xl border border-navy/15 bg-white p-5">
                            <p className="text-xs font-semibold uppercase tracking-wider text-fog">
                              Reply to {m.email} — sent from Sunkev Enterprises
                            </p>
                            <textarea
                              rows={4}
                              value={replyText}
                              onChange={(e) => setReplyText(e.target.value)}
                              placeholder="Write your reply…"
                              data-testid={`reply-text-${m.id}`}
                              className="mt-3 w-full resize-none rounded-xl border border-gray-200 px-4 py-3 text-sm text-ink outline-none transition-all duration-300 placeholder:text-gray-400 focus:border-navy focus:ring-4 focus:ring-navy/10"
                            />
                            <div className="mt-3 flex gap-2">
                              <button
                                onClick={() => sendReply(m.id)}
                                disabled={sending || !replyText.trim()}
                                data-testid={`reply-send-${m.id}`}
                                className="flex items-center gap-2 rounded-full bg-navy px-6 py-2.5 text-xs font-bold uppercase tracking-widest text-white transition-all duration-300 hover:bg-navy-800 disabled:opacity-50"
                              >
                                {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Send Reply"}
                              </button>
                              <button
                                onClick={() => setReplyOpen(null)}
                                className="rounded-full border border-gray-200 px-6 py-2.5 text-xs font-semibold text-fog hover:text-ink"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}
                      </article>
                    </Reveal>
                  );
                })}
              </div>
            )}
          </>
        ) : tab === "news" ? (
          <NewsPanel />
        ) : tab === "team" ? (
          <TeamPanel />
        ) : tab === "activity" ? (
          <ActivityPanel />
        ) : tab === "content" ? (
          <SiteContentPanel />
        ) : tab === "jobs" ? (
          <JobsPanel />
        ) : applications === null ? (
          <div className="flex justify-center py-24">
            <Loader2 className="h-8 w-8 animate-spin text-navy" />
          </div>
        ) : applications.length === 0 ? (
          <div className="mt-10 flex flex-col items-center rounded-3xl border border-gray-100 bg-white py-20 text-center">
            <Briefcase className="h-10 w-10 text-gray-300" />
            <p className="mt-4 font-heading text-lg font-bold text-ink">No applications yet</p>
            <p className="mt-1 text-sm text-fog">Careers-page applications with resumes will appear here.</p>
          </div>
        ) : (
          <div className="mt-6 space-y-4">
            <div className="flex flex-wrap gap-2" data-testid="application-filters">
              {APP_FILTERS.map((f) => {
                const count =
                  f === "all"
                    ? applications.length
                    : applications.filter((x) => (x.status || "new") === f).length;
                return (
                  <button
                    key={f}
                    onClick={() => setAppFilter(f)}
                    data-testid={`app-filter-${f}`}
                    className={`rounded-full px-5 py-2 text-xs font-bold uppercase tracking-widest transition-all duration-300 ${
                      appFilter === f
                        ? "bg-navy text-white"
                        : "border border-gray-200 bg-white text-fog hover:border-navy hover:text-navy"
                    }`}
                  >
                    {f} ({count})
                  </button>
                );
              })}
            </div>
            {visibleApps.map((a) => (
              <Reveal key={a.id}>
                <article
                  data-testid="application-card"
                  className="rounded-3xl border border-gray-100 bg-white p-7 shadow-sm"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="flex flex-wrap items-center gap-3">
                        <h2 className="font-heading text-lg font-bold text-ink">{a.role}</h2>
                        <span
                          data-testid={`app-status-${a.status || "new"}`}
                          className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest ${APP_STATUS_STYLES[a.status || "new"]}`}
                        >
                          {a.status || "new"}
                        </span>
                      </div>
                      <p className="mt-1 text-sm font-medium text-navy">{a.name}</p>
                      <div className="mt-2 flex flex-wrap gap-4 text-xs text-fog">
                        <span className="flex items-center gap-1.5">
                          <Mail className="h-3.5 w-3.5 text-gold" /> {a.email}
                        </span>
                        {a.phone && (
                          <span className="flex items-center gap-1.5">
                            <Phone className="h-3.5 w-3.5 text-gold" /> {a.phone}
                          </span>
                        )}
                      </div>
                    </div>
                    <span className="rounded-full bg-mist px-3 py-1 text-[11px] font-semibold text-fog">
                      {formatDate(a.created_at)}
                    </span>
                  </div>
                  {a.message && (
                    <p className="mt-4 whitespace-pre-line rounded-2xl bg-mist p-5 text-sm leading-relaxed text-ink">
                      {a.message}
                    </p>
                  )}
                  <div className="mt-5 flex flex-wrap items-center gap-2">
                    <button
                      onClick={() => downloadResume(a)}
                      data-testid={`download-resume-${a.id}`}
                      className="flex items-center gap-2 rounded-full bg-navy px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-white transition-all duration-300 hover:bg-navy-800"
                    >
                      <Download className="h-3.5 w-3.5" /> {a.resume_filename || "Download Resume"}
                    </button>
                    {(a.status || "new") !== "shortlisted" && (
                      <button
                        onClick={() => setAppStatus(a.id, "shortlisted")}
                        data-testid={`app-shortlist-${a.id}`}
                        className="rounded-full border border-gray-200 px-4 py-2 text-xs font-semibold text-fog transition-all duration-300 hover:border-royal hover:text-royal"
                      >
                        Shortlist
                      </button>
                    )}
                    {(a.status || "new") !== "hired" && (
                      <button
                        onClick={() => setAppStatus(a.id, "hired")}
                        data-testid={`app-hire-${a.id}`}
                        className="rounded-full border border-gray-200 px-4 py-2 text-xs font-semibold text-fog transition-all duration-300 hover:border-emerald-500 hover:text-emerald-600"
                      >
                        Hire
                      </button>
                    )}
                    {(a.status || "new") !== "rejected" && (
                      <button
                        onClick={() => setAppStatus(a.id, "rejected")}
                        data-testid={`app-reject-${a.id}`}
                        className="rounded-full border border-gray-200 px-4 py-2 text-xs font-semibold text-fog transition-all duration-300 hover:border-red-400 hover:text-red-600"
                      >
                        Reject
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setNoteOpen(noteOpen === a.id ? null : a.id);
                        setNoteText(a.note || "");
                      }}
                      data-testid={`app-note-${a.id}`}
                      className="rounded-full border border-gray-200 px-4 py-2 text-xs font-semibold text-fog transition-all duration-300 hover:border-gold hover:text-gold-dark"
                    >
                      {a.note ? "Edit Note" : "Add Note"}
                    </button>
                  </div>
                  {a.note && noteOpen !== a.id && (
                    <p className="mt-4 rounded-2xl border border-gold/20 bg-gold/5 p-4 text-sm italic leading-relaxed text-ink">
                      {a.note}
                    </p>
                  )}
                  {noteOpen === a.id && (
                    <div className="mt-4 rounded-2xl border border-navy/15 bg-white p-5">
                      <p className="text-xs font-semibold uppercase tracking-wider text-fog">
                        Private note — only visible to you
                      </p>
                      <textarea
                        rows={3}
                        value={noteText}
                        onChange={(e) => setNoteText(e.target.value)}
                        placeholder="e.g. Strong portfolio, schedule interview next week…"
                        data-testid={`note-text-${a.id}`}
                        className="mt-3 w-full resize-none rounded-xl border border-gray-200 px-4 py-3 text-sm text-ink outline-none transition-all duration-300 focus:border-navy focus:ring-4 focus:ring-navy/10"
                      />
                      <button
                        onClick={() => saveNote(a.id)}
                        data-testid={`note-save-${a.id}`}
                        className="mt-3 rounded-full bg-navy px-6 py-2.5 text-xs font-bold uppercase tracking-widest text-white transition-all duration-300 hover:bg-navy-800"
                      >
                        Save Note
                      </button>
                    </div>
                  )}
                </article>
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default Admin;
